import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  from,
  mergeMap,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { MemberType } from '../..//models/membership.status';
import { Prayer, PrayerStatus } from '../..//models/prayer.model';
import { UserModel } from '../..//models/user.model';
import { PrayerService } from '../../services/prayer.service';
import * as prayerActions from '../actions/prayer.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class PrayerEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  prayerService: PrayerService = inject(PrayerService);
  toastr: ToastrService = inject(ToastrService);
  private functions: AngularFireFunctions = inject(AngularFireFunctions);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);

  user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor(private location: Location) {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // effect to fetch prayers from Firestore
  getPrayers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(prayerActions.getPrayers),
      switchMap(({ groupId, memberType, lastRecord }) => {
        if (!this.user?.uid) return EMPTY;
        this.logout$.next();
        let query: AngularFirestoreCollection<Prayer> =
          this.afs.collection<Prayer>('prayers', (ref) => {
            var reference = ref.where('groupId', '==', groupId);

            if (memberType != MemberType.admin) {
              reference = reference.where('status', '==', PrayerStatus.active);
            }

            reference = reference.orderBy('modified', 'desc').limit(10);
            if (lastRecord != undefined) {
              reference = reference.startAfter(lastRecord);
            }
            return reference;
          });
        return query.get().pipe(
          takeUntil(this.logout$),
          switchMap((prayers) => {
            let prayersList: Prayer[] = prayers.docs.map((doc) => {
              const prayer = doc.data() as Prayer;
              prayer.id = doc.id;
              // console.log(`Prayer: ${JSON.stringify(prayer)}`);
              return prayer;
            });
            return of(
              prayerActions.loadPrayers({
                prayers: prayersList,
              })
            );
          }),
          catchError((error) => {
            // console.error(`Error fetching prayers: ${error}`);
            this.toastr.error(
              'An error occurred. Please try again later.',
              'Error Fetching Prayers'
            );
            return of(prayerActions.getPrayersFailure({ error }));
          })
        );
      })
    )
  );

  // effect to re-fetch recently created prayer
  getCreatedPrayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(prayerActions.createSuccess),
      switchMap(({ prayerId }) =>
        this.prayerService.getPrayerById(prayerId).pipe(
          switchMap((prayer) => of(prayerActions.loadPrayer({ prayer }))),
          catchError((error) => of(prayerActions.getPrayersFailure({ error })))
        )
      )
    )
  );

  // effect to fetch a single prayer by ID
  getPrayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(prayerActions.getPrayer),
      switchMap(({ prayerId }) =>
        this.prayerService.getPrayerById(prayerId).pipe(
          switchMap((prayer) => of(prayerActions.loadPrayer({ prayer }))),
          catchError((error) => of(prayerActions.getPrayersFailure({ error })))
        )
      )
    )
  );

  // effect to create a new prayer
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(prayerActions.create),
      mergeMap(async ({ prayer }) => {
        if (this.user == null) {
          this.toastr.error(
            'User not found. Kindly login again.',
            'Error Creating Prayer'
          );
          return prayerActions.createFailure({ error: 'User not found' });
        }
        const data: any = {
          ...prayer,
          created: serverTimestamp(),
          modified: serverTimestamp(),
          approvedBy: this.user.name,
          approvedById: this.user.uid,
          status: PrayerStatus.active,
        };

        return await this.prayerService
          .create(data)
          .then((prayerId) => {
            this.toastr.success(
              'The prayer has been created successfully.',
              'Prayer Created'
            );
            this.location.back();
            return prayerActions.createSuccess({
              prayerId: prayerId,
              groupId: prayer.groupId,
            });
          })
          .catch((error) => {
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Creating Prayer'
            );
            return prayerActions.createFailure({ error });
          });
      })
    )
  );

  // effect to update an existing prayer
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(prayerActions.update),
      mergeMap(async ({ prayer }) => {
        if (this.user == null) {
          this.toastr.error(
            'User not found. Kindly login again.',
            'Error Updating Prayer'
          );
          return prayerActions.updateFailure({ error: 'User not found' });
        }
        const data: any = {
          ...prayer,
          modified: serverTimestamp(),
          approvedBy: this.user.name,
          approvedById: this.user.uid,
        };

        // console.log(`Updating prayer: ${JSON.stringify(data, null, 2)}`);

        return await this.prayerService
          .update(prayer.id, data)
          .then(() => {
            this.toastr.success(
              'The prayer has been updated successfully.',
              'Prayer Updated'
            );
            this.location.back();
            return prayerActions.updateSuccess({ prayer });
          })
          .catch((error) => {
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Updating Prayer'
            );
            return prayerActions.updateFailure({ error });
          });
      })
    )
  );

  // Effect to update a prayer status
  updateStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(prayerActions.updateStatus),
      switchMap(({ id, prayer }) =>
        from(
          this.prayerService.update(id, {
            ...prayer,
            approvedBy: this.user?.name,
            approvedById: this.user?.uid,
            modified: serverTimestamp() as Timestamp,
          })
        ).pipe(
          switchMap(() => {
            const localPrayer: Prayer = {
              ...prayer,
              id: id,
              modified: Timestamp.fromDate(moment(prayer.modified).toDate()),
            };
            return of(prayerActions.updateStatusSuccess({ prayer: localPrayer }));
          }),
          catchError((error) => {
            console.error(`Error updating prayer: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Updating Prayer Status'
            );
            return of(prayerActions.updateStatusFailure({ error }));
          })
        )
      )
    )
  );

  // effect to delete a prayer
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(prayerActions.deleteAction),
      switchMap(({ id }) =>
        this.prayerService
          .delete(id)
          .then(() => {
            this.toastr.success(
              'The prayer has been deleted successfully.',
              'Prayer Deleted'
            );
            this.location.back();
            return prayerActions.deleteSuccess({ id: id });
          })
          .catch((error) => {
            // console.error(`Error deleting prayer: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Prayer'
            );
            return prayerActions.deleteFailure({ error });
          })
      )
    )
  );

  // Get Prayers Statistics
  getPrayerStatistics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        prayerActions.getPrayerStats,
        prayerActions.createSuccess,
        prayerActions.updateStatusSuccess
      ),
      switchMap((action) => {
        let groupId = '';
        if (action.type === prayerActions.getPrayerStats.type) {
          groupId = action.groupId;
        } else if (action.type === prayerActions.createSuccess.type) {
          groupId = action.groupId;
        } else if (action.type === prayerActions.updateStatusSuccess.type) {
          groupId = action.prayer.groupId;
        }
        return from(this.prayerService.getPrayerStats(groupId)).pipe(
          switchMap((snapshot) => {
            let g: any = {
              id: groupId,
              requestedPrayers: snapshot?.request ?? '-',
              openedPrayers: snapshot?.opened ?? '-',
              closedPrayers: snapshot?.closed ?? '-',
            };
            return of(prayerActions.getPrayerStatsDone({ group: g }));
          }),
          catchError((error) =>
            of(prayerActions.getPrayerStatsFailure({ error }))
          )
        );
      })
    )
  );

  // Effect to clear prayer state on user logout
  clearPrayers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(prayerActions.clear());
      })
    )
  );
}
